/**
 * passwordLinking — a signed-in Google-only person sets a password, so both
 * Google and email/password open the SAME account (triage
 * tri_mu32bbfv_109f93883d, scope item 2; founder ruling 2026-09-15).
 *
 * The Firebase call is linkWithCredential(currentUser, EmailAuthProvider
 * .credential(email, password)) — the uid does not change, so the canonical
 * email→uid mapping the sign-in resolver reads is untouched, and Google keeps
 * working. Firebase demands a recent sign-in for it: on
 * auth/requires-recent-login we re-authenticate with Google (a popup the
 * person already knows) and retry ONCE. Nothing here imports firebase/auth —
 * the two Firebase calls arrive as injected functions, so the decision and
 * error mapping are unit-testable in node and the Settings card that renders
 * this never drags firebase/auth into its import graph (the attachmentStorage
 * lesson in CLAUDE.md).
 */

export const GOOGLE_PROVIDER_ID = 'google.com';
export const PASSWORD_PROVIDER_ID = 'password';

export interface SignInMethodsView {
  hasGoogle: boolean;
  hasPassword: boolean;
  /** Google is a method and password is not — the account this feature is for. */
  googleOnly: boolean;
}

/** Reduce User.providerData's provider ids to what the settings card shows. */
export function describeSignInMethods(providerIds: readonly string[] | null | undefined): SignInMethodsView {
  const ids = Array.isArray(providerIds) ? providerIds : [];
  const hasGoogle = ids.includes(GOOGLE_PROVIDER_ID);
  const hasPassword = ids.includes(PASSWORD_PROVIDER_ID);
  return { hasGoogle, hasPassword, googleOnly: hasGoogle && !hasPassword };
}

/** Firebase's own floor is 6 characters; we say so before calling it. */
export const MIN_PASSWORD_LENGTH = 6;

/** The client-side check before any network call. Returns the problem, or
 *  null when the pair is acceptable. */
export function validateNewPassword(password: string, confirm: string): string | null {
  if (!password || password.length < MIN_PASSWORD_LENGTH) {
    return `your password needs at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  if (password !== confirm) return "those two passwords don't match — type the same one in both boxes.";
  return null;
}

export type LinkPasswordOutcome =
  | { status: 'linked' }
  | { status: 'error'; code: string; message: string };

export interface LinkPasswordDeps {
  /** linkWithCredential(currentUser, EmailAuthProvider.credential(email, password)). */
  link: (password: string) => Promise<void>;
  /** reauthenticateWithPopup(currentUser, new GoogleAuthProvider()). */
  reauthenticateWithGoogle: () => Promise<void>;
}

/** House-voice copy for the ways linking can fail (D9 failure copy). */
export function linkPasswordMessageForCode(code: string): string {
  switch (code) {
    case 'auth/weak-password':
      return 'that password is too easy to guess — try a longer one.';
    case 'auth/provider-already-linked':
      return 'this account already has a password. use "forgot password?" on the sign-in page to change it.';
    case 'auth/email-already-in-use':
    case 'auth/credential-already-in-use':
      return "a different account already uses this email with a password — sign in with google here, and we'll help sort out the duplicate.";
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'the google window closed before we could confirm it was you — try again when you have a moment.';
    case 'auth/popup-blocked':
      return 'your browser blocked the google window we need to confirm it was you. allow popups for prest0 and try again.';
    case 'auth/requires-recent-login':
      return "we couldn't confirm it was you just now — sign out, sign back in with google, and try again.";
    case 'auth/network-request-failed':
      return 'network hiccup — check your connection and try again.';
    case 'auth/no-current-user':
      return "you're not signed in any more — sign in with google and come back here.";
    default:
      return "couldn't set the password just now — please try again in a moment.";
  }
}

/**
 * Link a password to the signed-in account. Re-authenticates with Google and
 * retries once when Firebase asks for a fresh sign-in; every other failure is
 * reported with its code and house-voice copy.
 */
export async function linkPasswordToSignedInUser(
  password: string,
  deps: LinkPasswordDeps,
): Promise<LinkPasswordOutcome> {
  const codeOf = (e: unknown) => String((e as { code?: unknown } | null)?.code || 'UNKNOWN');
  try {
    await deps.link(password);
    return { status: 'linked' };
  } catch (first) {
    const firstCode = codeOf(first);
    if (firstCode !== 'auth/requires-recent-login') {
      return { status: 'error', code: firstCode, message: linkPasswordMessageForCode(firstCode) };
    }
    try {
      await deps.reauthenticateWithGoogle();
    } catch (reauth) {
      const c = codeOf(reauth);
      return { status: 'error', code: c, message: linkPasswordMessageForCode(c) };
    }
    try {
      await deps.link(password);
      return { status: 'linked' };
    } catch (second) {
      const c = codeOf(second);
      return { status: 'error', code: c, message: linkPasswordMessageForCode(c) };
    }
  }
}
